<template>
  <div class="hSwiperWrap" :class="{ hSwiperWrapPc: isDesktop && data.length > 1 }"
       @mousedown="onDragStart" @click.capture="onWrapClickCapture" @dragstart.prevent>
    <van-swipe ref="swipeRef" :autoplay="isDesktop ? 5000 : 3000" lazy-render :style='{
    margin: HNewGameUIConfig.newGameUIType === 4?"6px 6px 0 6px":HNewGameUIConfig.newGameUIType === 0?"0 10px":"0",
    borderRadius: noRadius ? "0" : (HNewGameUIConfig.newGameUIType === 4?"10px":HNewGameUIConfig.newGameUIType === 0?"4px":"0"),
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    paddingBottom: indicatorBelow ? "20px" : "",
  }'>
      <van-swipe-item v-for="eachData in (data.length<=0?tempData:data)" @click="clickBanner(eachData)"
                      :key="eachData['Icon']">
        <HCacheImage :src="eachData['Icon']" :height="height??'100%'" :width="width??'100%'"/>
      </van-swipe-item>
      <template v-if="data.length>1" #indicator="{ active, total }">
        <div class="custom-indicator" :style="{bottom: indicatorBelow ? '6px' : '5px'}">
          <div :style="indicatorBelow
              ? {display:'flex',justifyContent:'center',alignItems:'center'}
              : {backgroundColor:'#0003',padding:'5px',borderRadius:'10px',display:'flex',justifyContent:'center',alignItems:'center'}">
            <div v-for="num in total" :key="num"
                 @click.stop="swipeRef?.swipeTo(num-1)"
                 :class="['eachIndicator', num-1 === active?(HNewGameUIConfig.newGameUIType === 0?'eachIndicatorActiveMain':'eachIndicatorActiveOther'):'']"/>
          </div>
        </div>
      </template>
    </van-swipe>
    <!-- 桌面: Vant swipe 只支持触摸拖动, 鼠标无法滑动; 加左右箭头手动切换(悬停显示) -->
    <template v-if="isDesktop && data.length > 1">
      <div class="hSwiperArrow hSwiperArrowLeft" @click.stop="swipeRef?.prev()">‹</div>
      <div class="hSwiperArrow hSwiperArrowRight" @click.stop="swipeRef?.next()">›</div>
    </template>
  </div>
</template>


<script setup>
  import HCacheImage from "@/components/utils/HCacheImage.vue";
  import {useClickModal} from "@/hooks/useClickModal.js";
  import {hdi} from "@/components/utils/HDeviceInfo.js";
  import HNewGameUIConfig from "@/components/utils/HNewGameUIConfig.js";
  import {useResponsive} from "@/hooks/useResponsive.js";

  let tempData = ["https://"];
  const props = defineProps(["data", "height", "width", "indicatorBelow", "noRadius"]);
  const emit = defineEmits(["itemClick"]);
  const clickModal = useClickModal();
  const {isDesktop} = useResponsive();
  const swipeRef = ref(null);

  function clickBanner(eachBannerData) {
    clickModal.onClick(eachBannerData["ClickItem"]);
    emit("itemClick", eachBannerData);
  }

  // 桌面: Vant swipe 只认触摸事件, 鼠标拖不动, 且拖 <img> 会触发原生图片拖拽(虚影)。
  // 这里把鼠标拖动「合成为触摸事件」喂给 swipe 根元素 → 跟手移动 + 松手吸附切换(体验同触摸);
  // 不支持合成 TouchEvent 的浏览器(如桌面 Safari)回退为「拖过阈值切上/下一张」。
  let dragStartX = 0;
  let dragging = false;
  let dragMoved = false;
  let suppressClickOnce = false;
  let swipeEl = null;

  // 能否用构造函数合成触摸事件(Chrome/Edge 可; 桌面 Safari 不行)
  const canSynthTouch = typeof window !== 'undefined'
    && typeof window.TouchEvent === 'function'
    && typeof window.Touch === 'function'
    && (() => { try { new window.TouchEvent('touchstart'); return true; } catch (e) { return false; } })();

  function fireTouch(type, x, y) {
    if (!swipeEl) return;
    const t = new window.Touch({identifier: 1, target: swipeEl, clientX: x, clientY: y, pageX: x, pageY: y});
    const isEnd = type === 'touchend';
    swipeEl.dispatchEvent(new window.TouchEvent(type, {
      bubbles: true, cancelable: true, composed: true,
      touches: isEnd ? [] : [t],
      targetTouches: isEnd ? [] : [t],
      changedTouches: [t],
    }));
  }

  function onDragMove(e) {
    if (!dragging) return;
    if (Math.abs(e.clientX - dragStartX) > 5) dragMoved = true;
    if (canSynthTouch) fireTouch('touchmove', e.clientX, e.clientY);
  }

  function onDragEnd(e) {
    if (!dragging) return;
    dragging = false;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
    if (canSynthTouch) {
      fireTouch('touchend', e.clientX, e.clientY); // 交给 Vant 吸附到最近一张
    } else {
      // 回退: 阈值切换
      const dx = e.clientX - dragStartX;
      if (dragMoved && Math.abs(dx) > 40 && swipeRef.value) {
        dx < 0 ? swipeRef.value.next() : swipeRef.value.prev();
      }
    }
    if (dragMoved) {
      suppressClickOnce = true; // 拦掉这次拖动尾部的 click(避免误开 banner)
      setTimeout(() => { suppressClickOnce = false; }, 350);
    }
  }

  function onDragStart(e) {
    if (!isDesktop.value || props.data.length <= 1) return;
    if (e.button !== 0) return; // 仅左键
    dragStartX = e.clientX;
    dragging = true;
    dragMoved = false;
    // 不在此 preventDefault: 项目用 default-passive-events 把 mousedown 变成被动监听, 调用会报错。
    // 图片原生拖拽 / 文本选中已由模板 @dragstart.prevent + CSS(user-drag:none / user-select:none)阻止。
    // 关键: Vant4 的 touch 监听(touchstart/touchmove/touchend)绑在 .van-swipe__track 上(root 的子元素),
    // 事件冒泡是子→父, 若把合成 touch 派发到 root($el)则永远到不了 track 的监听器 → 拖动完全无效。
    // 必须派发到 track 本身, 才能跟手移动 + 松手吸附。
    swipeEl = swipeRef.value?.$el?.querySelector('.van-swipe__track') || swipeRef.value?.$el || null;
    if (canSynthTouch) fireTouch('touchstart', e.clientX, e.clientY);
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
  }

  function onWrapClickCapture(e) {
    if (suppressClickOnce) {
      suppressClickOnce = false;
      e.stopPropagation();
      e.preventDefault();
    }
  }

</script>


<style scoped>
  .custom-indicator {
    display: flex;
    flex-direction: row;
    position: absolute;
    right: 0;
    bottom: 5px;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 2px 5px;
    font-size: 12px;
  }

  .eachIndicator {
    width: 4px;
    height: 4px;
    margin-right: 5px;
    border-radius: 20px;
    background-color: #ccc
  }

  .eachIndicatorActiveMain {
    width: 15px;
    background-color: #ffae00;
  }

  .eachIndicatorActiveOther {
    width: 15px;
    background-color: var(--ct-main-color);
  }

  /* 箭头定位基准 */
  .hSwiperWrap {
    position: relative;
  }

  /* 桌面可拖动: 抓手光标提示 + 禁用图片原生拖拽/文本选中(否则鼠标拖会拖出图片虚影) */
  .hSwiperWrapPc {
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
  }
  .hSwiperWrapPc:active {
    cursor: grabbing;
  }
  .hSwiperWrapPc :deep(img) {
    -webkit-user-drag: none;
    user-drag: none;
  }

  /* 桌面: <style> 里的 4px/15px 会被转 vw(宽屏放大到 ~15px 圆点、~58px 长条, 过于显眼),
     拉回真实 px 保持小巧。 */
  @media (min-width: 1024px) {
    .custom-indicator {
      font-size: 12px;
      padding: 2px 5px;
    }
    .eachIndicator {
      width: 5px;
      height: 5px;
      margin-right: 5px;
      border-radius: 20px;
      cursor: pointer;
    }
    .eachIndicatorActiveMain,
    .eachIndicatorActiveOther {
      width: 16px;
    }

    /* 左右切换箭头: 默认半透明, 悬停整个 banner 时显现 */
    .hSwiperArrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 5;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.35);
      color: #fff;
      font-size: 24px;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.2s, background 0.2s;
      user-select: none;
    }
    .hSwiperWrap:hover .hSwiperArrow {
      opacity: 1;
    }
    .hSwiperArrow:hover {
      background: rgba(0, 0, 0, 0.55);
    }
    .hSwiperArrowLeft {
      left: 18px;
      padding-bottom: 3px;
    }
    .hSwiperArrowRight {
      right: 18px;
      padding-bottom: 3px;
    }
  }
</style>
